import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { formatISODate, timestampToDate } from 'src/utils/dateFormatter';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import {
  centsToDollars,
  dollarsToCents,
  removeReadOnlyFields,
} from 'src/utils/fieldNormalizers';

import { PayoutSchedule } from '../location/_types';
import { User } from '../user/_types';

import {
  BusinessSubscription,
  Charge,
  CustomerBalanceTransaction,
  Invoice,
  Payouts,
  ServiceBalanceTransaction,
  StripePlan,
  StripeSubscription,
  Subscription,
  SubscriptionReportParams,
  UpcomingCharge,
} from './_types';
import { UpcomingChargeParams } from './payment';

export function normalizeSubscription(subscription: any): Subscription {
  const camelized = camelcaseKeys(subscription, { deep: true });

  if (camelized.startOn) {
    const [year, month, day] = camelized.startOn.split('-').map(Number);
    camelized.startOn = new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  if (camelized.cancelOn) {
    const [year, month, day] = camelized.cancelOn.split('-').map(Number);
    camelized.cancelOn = new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  return {
    ...camelized,
  } as Subscription;
}

export function denormalizeSubscription(pkg: Partial<Subscription>): any {
  return decamelizeKeys(pkg, { deep: true });
}
export function normalizeCharge(charge: any): Charge {
  const camelized = camelcaseKeys(charge, { deep: true });
  return {
    ...camelized,
    amount: centsToDollars(camelized.amount),
    amountRefunded: centsToDollars(camelized.amountRefunded),
    tax: centsToDollars(camelized.tax),
  };
}

export function normalizeInvoice(invoice: any): Invoice {
  const camelized = camelcaseKeys(invoice, { deep: true });
  return {
    ...camelized,
    amountDue: centsToDollars(camelized.amountDue),
    amountPaid: centsToDollars(camelized.amountPaid),
    endingBalance: centsToDollars(camelized.endingBalance),
    startingBalance: centsToDollars(camelized.startingBalance),
    balanceAdjustment: centsToDollars(camelized.balanceAdjustment),
    ...(camelized.chargeDate && {
      chargeDate: timestampToDate(camelized.chargeDate),
    }),
    ...(camelized.dueDate && { dueDate: timestampToDate(camelized.dueDate) }),
  };
}

export function denormalizeInvoice(invoice: Partial<Invoice>): any {
  const decamelized = decamelizeKeys(invoice, { deep: true });
  removeReadOnlyFields(decamelized, READO_ONLY_INVOICE_FIELDS);
  return {
    ...decamelized,
    ...(decamelized.amount_due && {
      amount_due: dollarsToCents(decamelized.amount_due),
    }),
    ...(decamelized.due_date && {
      due_date: getUnixTime(decamelized.due_date),
    }),
    ...(decamelized.charge_date && {
      charge_date: getUnixTime(decamelized.charge_date),
    }),
  };
}

export function normalizeCustomerBalanceTransaction(
  transaction: any
): CustomerBalanceTransaction {
  const camelized = camelcaseKeys(transaction, { deep: true });
  return {
    ...camelized,
    amount: centsToDollars(camelized.amount),
    endingBalance: centsToDollars(camelized.endingBalance),
    startingBalance: centsToDollars(camelized.startingBalance),
  };
}

export function denormalizeCustomerBalanceTransaction(
  transaction: Partial<CustomerBalanceTransaction>
): any {
  const decamelized = decamelizeKeys(transaction, { deep: true });
  return {
    ...decamelized,
    amount: dollarsToCents(decamelized.amount || 0),
  };
}

export function normalizeServiceBalanceTransaction(
  transaction: any
): ServiceBalanceTransaction {
  const camelized = camelcaseKeys(transaction, { deep: true });
  return {
    ...camelized,
    amount: centsToDollars(camelized.amount),
    endingBalance: centsToDollars(camelized.endingBalance),
    startingBalance: centsToDollars(camelized.startingBalance),
  };
}

export function denormalizeServiceBalanceTransaction(
  transaction: Partial<ServiceBalanceTransaction>
): any {
  const decamelized = decamelizeKeys(transaction, { deep: true });
  return {
    ...decamelized,
    amount: dollarsToCents(decamelized.amount || 0),
  };
}

// Normalization function
export function normalizeStripeSubscription(
  subscription: any,
  userId?: User['id']
): StripeSubscription {
  const camelized = camelcaseKeys(subscription, { deep: true });

  if (camelized.startOn) {
    const [year, month, day] = camelized.startOn.split('-').map(Number);
    camelized.startOn = new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  if (camelized.cancelOn) {
    const [year, month, day] = camelized.cancelOn.split('-').map(Number);
    camelized.cancelOn = new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  if (camelized.stripeResumeAt) {
    const [year, month, day] = camelized.stripeResumeAt.split('-').map(Number);
    camelized.stripeResumeAt = new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  if (camelized.stripePauseOn) {
    const [year, month, day] = camelized.stripePauseOn.split('-').map(Number);
    camelized.stripePauseOn = new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  if (camelized.couponResumeOn) {
    const [year, month, day] = camelized.couponResumeOn.split('-').map(Number);
    camelized.couponResumeOn = new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  if (camelized.couponPauseOn) {
    const [year, month, day] = camelized.couponPauseOn.split('-').map(Number);
    camelized.couponPauseOn = new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  if (camelized.currentPeriodStart) {
    camelized.currentPeriodStart = fromUnixTime(camelized.currentPeriodStart);
  }

  if (camelized.currentPeriodEnd) {
    camelized.currentPeriodEnd = fromUnixTime(camelized.currentPeriodEnd);
  }

  if (camelized.contractStartDate) {
    camelized.contractStartDate = fromUnixTime(camelized.contractStartDate);
  }

  if (camelized.platformId) {
    camelized.platformId = parseInt(camelized.platformId, 10);
  }

  if (camelized.pauseCollection) {
    camelized.pauseCollection = {
      ...camelized.pauseCollection,
      resumes_at: camelized.pauseCollection.resumes_at
        ? fromUnixTime(camelized.pauseCollection.resumes_at)
        : null,
    };
  }

  return { ...camelized, ...(userId && { userId }) };
}

export function denormalizeStripeSubscription(
  subscription: Partial<StripeSubscription>
): any {
  const decamelized = decamelizeKeys(subscription, { deep: true });
  if (decamelized.start_on) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - server expects ISO string for dates
    decamelized.start_on = formatISODate(decamelized.start_on);
  }

  return decamelized;
}

export function normalizeStripePlan(plan: any): StripePlan {
  const camelized = camelcaseKeys(plan, { deep: true });
  return camelized;
}

export function denormalizeStripePlan(plan: Partial<StripePlan>): any {
  const decamelized = decamelizeKeys(plan, { deep: true });

  removeReadOnlyFields(decamelized.metadata, ['platform_id', 'platform_brand']);
  removeReadOnlyFields(decamelized, [
    'active',
    'recurring',
    'type',
    'object',
    'created',
    'livemode',
    'lookup_key',
    'tiers_mode',
    'billing_scheme',
    'custom_unit_amount',
    'recurring',
    'transform_quantity',
    'unit_amount_decimal',
  ]);

  if (decamelized.amount) {
    decamelized.amount = dollarsToCents(decamelized.amount);
  }

  return decamelized;
}

export function normalizeBusinessSubscription(
  subscription: any
): BusinessSubscription {
  const camelized = camelcaseKeys(subscription, { deep: true });
  return {
    ...camelized,
    subscription: {
      ...camelized.subscription,
      ...{
        user: camelized.user,
        userId: camelized.user.id,
        clientId: camelized.clientId,
        subscribeableName: camelized.subscribeable.name,
      },
    } as StripeSubscription,
  } as BusinessSubscription;
}

export function normalizeFetchBusinessSubscriptionsParams({
  per,
  page,
  couponCode,
  subscribeableId,
  subscribeableType,
  userSearch,
  startOnNotNull,
  inactive,
  willCancel,
  isPaused,
  startDate,
  endDate,
  sortBy,
  sortDir,
  pastDue,
}: SubscriptionReportParams): any {
  const userSearchParam = userSearch
    ? userSearch.split(' ').length > 1
      ? {
          'q[user_first_name_cont]': userSearch.split(' ')[0],
          'q[user_last_name_cont]': userSearch.split(' ')[1],
        }
      : {
          'q[user_first_name_or_user_last_name_or_user_email_cont]': userSearch,
        }
    : {};
  return {
    per,
    page,
    ...(couponCode && { 'q[has_coupon_code]': couponCode }),
    ...(subscribeableId &&
      subscribeableId !== 'null' && {
        'q[subscribeable_id_eq]': subscribeableId,
      }),
    ...(subscribeableType && {
      'q[subscribeable_type_eq]': subscribeableType,
    }),
    ...userSearchParam,
    ...(startOnNotNull !== undefined &&
      (startOnNotNull
        ? {
            'q[start_on_not_null]': 1,
          }
        : {
            'q[start_on_null]': 1,
          })),
    ...(inactive !== undefined && { inactive: 1 }),
    ...(willCancel !== undefined &&
      (willCancel
        ? {
            'q[will_cancel]': 1,
          }
        : {
            'q[will_not_cancel]': 1,
          })),
    ...(isPaused !== undefined &&
      (isPaused
        ? {
            'q[is_paused]': 1,
          }
        : {
            'q[is_not_paused]': 1,
          })),
    ...(startDate && { start_date: getUnixTime(new Date(startDate)) }),
    ...(endDate && { end_date: getUnixTime(new Date(endDate)) }),
    ...(sortBy && sortDir && { 'q[s]': `${sortBy} ${sortDir}` }),
    ...(pastDue !== undefined &&
      (pastDue
        ? {
            'q[past_due]': 1,
          }
        : {
            'q[not_past_due]': 1,
          })),
  };
}

export function normalizeUpcomingCharge(charge: any): UpcomingCharge {
  const camelized = camelcaseKeys(charge, { deep: true });
  return camelized;
}

export function upcomingChargesParams(params: UpcomingChargeParams): any {
  const decamelized = decamelizeKeys(params, { deep: true });
  return {
    ...decamelized,
    ...(decamelized.start_date && {
      start_date: getUnixTime(new Date(decamelized.start_date)),
    }),
    ...(decamelized.end_date && {
      end_date: getUnixTime(new Date(decamelized.end_date)),
    }),
  };
}

export function normalizePayouts(payouts: any): Payouts {
  const camelized = camelcaseKeys(payouts, { deep: true });
  camelized.available = centsToDollars(camelized.available);
  return camelized;
}

export function denormalizeUpdatePayoutScheduleParams(
  schedule: PayoutSchedule
) {
  const params = { interval: schedule.interval };
  if (schedule.interval === 'weekly') {
    (params as any).weekly_anchor = schedule.weeklyAnchor;
  }
  if (schedule.interval === 'monthly') {
    (params as any).monthly_anchor = schedule.monthlyAnchor;
  }
  return params;
}

const READO_ONLY_INVOICE_FIELDS = [
  'id',
  'item_name',
  'amount_paid',
  'attempt_count',
  'charge_id',
  'application_fee_amount',
  'currency',
  'paid_at',
  'next_payment_attempt',
  'name',
  'email',
  'hosted_invoice_url',
  'invoice_pdf',
  'client_id',
  'charge_failure',
  'last_charge_attempt',
  'starting_balance',
  'ending_balance',
  'balance_adjustment',
  'item_delivered',
  'charge_status',
  'created_at',
  'updated_at',
];
